import { createAsyncThunk } from "@reduxjs/toolkit";

import client from "@/client/apollo";
import { __GLOBAL_TENANT } from "@/client/globals";
import jitsuClient from "@/client/jitsu";
import { DEPLOY_ENV, DeployEnv, TIMELINE_URL } from "@/config";
import {
  ApiTokenDocument,
  ApiTokenQuery,
  ApiTokenQueryVariables,
  BackgroundJobDocument,
  BackgroundJobQuery,
  BackgroundJobQueryVariables,
  BackgroundJobState,
  CreateGroupsDocument,
  CreateGroupsMutation,
  CreateGroupsMutationVariables,
  ExtensionConflictActionType,
  GenerateReportDocument,
  GenerateReportMutation,
  GenerateReportMutationVariables,
  InsertSyncRosterJobDocument,
  InsertSyncRosterJobMutation,
  InsertSyncRosterJobMutationVariables,
  UpdateGroupMembershipsDocument,
  UpdateGroupMembershipsMutation,
  UpdateGroupMembershipsMutationVariables,
} from "@/generated/graphql";

import { JitsuEvent } from "../events/JitsuEvent";

//////////////////////////////////////////////////////////////////////////////
// Roster Sync mutation                                                     //
//////////////////////////////////////////////////////////////////////////////

/**
 * Trigger a class roster sync for the Assessment.
 *
 * The process itself is synchronous from the client perspective, but may use
 * different modes of operation on the backend:
 *
 *   1. For smaller classes, a single mutation is used to sync the roster in one
 *      go. Consider this soft deprecated.
 *   2. For larger classes, a background job is created to perform the sync, and
 *      we await the completion of the job by polling. This will become the
 *      default in the future. Currently trialing it for cadmus tenants only.
 *
 */
export interface SyncClassListActionPayload {
  assessmentId: string;
  withExtensions?: boolean;
  extensionConflictAction?: ExtensionConflictActionType;
  /** Prevent progress toasts from showing in the UI */
  supressToasts?: boolean;
}
export const syncClassList = createAsyncThunk(
  "class/sync",
  async (payload: SyncClassListActionPayload, _thunkAPI) => {
    await syncClassListWithBackgroundJob(payload);
    return { enrollmentCount: undefined };
  }
);

/**
 * Synchronous class list sync using a background job to perform the sync, with
 * the promise awaiting the completion of the job.
 *
 * This is suitable for larger classes where the operation may take longer to
 * complete, and the backend processes it in the background.
 *
 * The Return value is not important, as the actual enrollments will be updated
 * via subscription or refetch. Returns a promise that completes (resolves or
 * rejects) when the background job is done.
 */
async function syncClassListWithBackgroundJob(
  payload: SyncClassListActionPayload
): Promise<void> {
  const { assessmentId, withExtensions, extensionConflictAction } = payload;

  const result = await client.mutate<
    InsertSyncRosterJobMutation,
    InsertSyncRosterJobMutationVariables
  >({
    mutation: InsertSyncRosterJobDocument,
    variables: {
      assessmentId,
      withExtensions: Boolean(withExtensions),
      extensionConflictAction: extensionConflictAction || null,
    },
  });

  if (result.data?.insertSyncRosterJob) {
    const jobId = result.data.insertSyncRosterJob.id;
    await pollJobCompletion(jobId);
  }
}

/**
 * Use a Query to poll for the completion of the background job with ID `jobId`,
 * every `intervalMs` milliseconds.
 */
async function pollJobCompletion(
  jobId: string,
  intervalMs = 1000
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const executePoll = async () => {
      try {
        const jobResult = await client.query<
          BackgroundJobQuery,
          BackgroundJobQueryVariables
        >({
          query: BackgroundJobDocument,
          variables: { jobId },
          fetchPolicy: "network-only",
        });
        const state = jobResult.data.backgroundJob?.state;
        if (
          state === BackgroundJobState.Completed ||
          state === BackgroundJobState.Cancelled
        ) {
          resolve();
        } else if (state === BackgroundJobState.Discarded) {
          reject(new Error("Background job failed"));
        } else if (state === undefined) {
          reject(new Error("Background job not found"));
        } else {
          setTimeout(executePoll, intervalMs);
        }
      } catch (error) {
        reject(error);
      }
    };

    executePoll();
  });
}

//////////////////////////////////////////////////////////////////////////////
// Assessment report generation                                             //
//////////////////////////////////////////////////////////////////////////////

export const generateAssessmentReport = createAsyncThunk(
  "class/generateAssessmentReport",
  async (payload: { assessmentId: string; email: string }, _thunkAPI) => {
    const { assessmentId, email } = payload;

    await client.mutate<
      GenerateReportMutation,
      GenerateReportMutationVariables
    >({
      mutation: GenerateReportDocument,
      variables: {
        kind: "assessment_summary",
        assessmentId,
        workId: null,
        anonymousName: null,
        studentId: null,
      },
    });

    return email;
  }
);

interface GenerateActivityReportPayload {
  assessmentId: string;
  workId: string;
  anonymousName?: string;
  /** Narrows the report to a single student's contribution to the Work. */
  studentId?: string;
}

interface TimelineAccessPayload {
  workId: string;
  assessmentId: string;
}

/**
 * Trigger mutation to generate a Student Activity report and open a new tab to
 * the successfully generated report URL.
 */
export const generateActivityReport = createAsyncThunk(
  "class/generateActivityReport",
  async (payload: GenerateActivityReportPayload, thunkAPI) => {
    const { assessmentId, workId, anonymousName, studentId } = payload;
    const { data } = await client.mutate<
      GenerateReportMutation,
      GenerateReportMutationVariables
    >({
      mutation: GenerateReportDocument,
      variables: {
        kind: "student_activity",
        assessmentId,
        workId,
        anonymousName: anonymousName ?? null,
        studentId: studentId ?? null,
      },
    });

    const resultUrl = data?.generateReport?.resultUrl;

    if (resultUrl) {
      const win = window.open(resultUrl);
      if (win) {
        win.focus();
      }

      return resultUrl;
    }

    return thunkAPI.rejectWithValue("Unable to generate report URL");
  }
);

/**
 * Trigger query to fetch timeline URL.
 */
export const timelineAccess = createAsyncThunk(
  "class/timelineAccess",
  async (payload: TimelineAccessPayload, thunkAPI) => {
    const { workId, assessmentId } = payload;

    // Fetch API token
    const { data: tokenData } = await client.query<
      ApiTokenQuery,
      ApiTokenQueryVariables
    >({
      query: ApiTokenDocument,
      variables: {
        assessmentId,
        workId,
      },
    });

    const apiToken = tokenData?.apiToken;
    const tenant = __GLOBAL_TENANT.current;
    const environment = fetchEnvironment();

    if (!apiToken) {
      return thunkAPI.rejectWithValue("Unable to fetch API token");
    }

    if (!tenant) {
      return thunkAPI.rejectWithValue("Tenant not available");
    }

    // Construct timeline URL
    const params = new URLSearchParams({
      token: apiToken,
      workId,
      tenant,
    });

    const timelineUrl = `${TIMELINE_URL}/${environment}/player?${params.toString()}`;

    const win = window.open(timelineUrl);
    if (win) {
      win.focus();
    }

    jitsuClient.track(JitsuEvent.REPORT_REQUESTED, {
      report_type: "TIMELINE",
      assessment_id: assessmentId,
      work_id: workId,
    });

    return timelineUrl;
  }
);

const fetchEnvironment = () => {
  switch (DEPLOY_ENV) {
    case DeployEnv.LOCAL:
      return "local";
    case DeployEnv.STAGING:
      return "staging";
    case DeployEnv.PRODUCTION_AU:
      return "au";
    case DeployEnv.PRODUCTION_UK:
      return "uk";
    case DeployEnv.PRODUCTION_ME:
      return "me";
    case DeployEnv.PRODUCTION_US:
      return "us";
    default:
      return "au";
  }
};

//////////////////////////////////////////////////////////////////////////////
// Bulk groups update mutataion                                             //
//////////////////////////////////////////////////////////////////////////////

/**
 * Move students into an existing or new group, updating multiple group
 * memberships as needed.
 */
export const updateGroupMemberships = createAsyncThunk(
  "class/updateGroupMemberships",
  async (
    payload: {
      /** Cadmus Assessment ID */
      assessmentId: string;
      /** Existing or new group to move students into. */
      group: { kind: "existing"; id: string } | { kind: "new"; name: string };
      /** Student Ids whose group membership is changing */
      studentIds: string[];
    },
    _thunkAPI
  ) => {
    const { assessmentId, group, studentIds } = payload;

    // Move students to an exsiting group
    if (group.kind === "existing") {
      return updateMembershipsMutation(assessmentId, group.id, studentIds);
    }
    // Move students to a new group by first creating it
    const result = await client.mutate<
      CreateGroupsMutation,
      CreateGroupsMutationVariables
    >({
      mutation: CreateGroupsDocument,
      variables: {
        assessmentId,
        groups: [{ name: group.name, members: [], code: null, groupSet: null }],
      },
    });

    const newGroup = result?.data?.createGroups?.find(
      (g) => g.name === group.name
    );
    const groupId = newGroup?.id;

    if (groupId) {
      return updateMembershipsMutation(assessmentId, groupId, studentIds);
    }

    return null;
  }
);

async function updateMembershipsMutation(
  assessmentId: string,
  groupId: string,
  memberIds: string[]
) {
  const memberships = memberIds.map((userId) => ({
    groupId,
    userId,
  }));

  const result = await client.mutate<
    UpdateGroupMembershipsMutation,
    UpdateGroupMembershipsMutationVariables
  >({
    mutation: UpdateGroupMembershipsDocument,
    variables: {
      assessmentId,
      memberships,
    },
  });

  return result;
}
